// DesktopNav.js - Horizontal desktop navigation with Admin dropdown
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import NavLink from './NavLink';
import { getVisibleGroups } from '../../config/navigation';

const DesktopNav = ({ isAuthenticated, adminLevel, onLogout, customerName }) => {
  const groups = getVisibleGroups(isAuthenticated, adminLevel);
  const flatItems = groups.filter(g => !g.dropdown).flatMap(g => g.items);
  const dropdownGroups = groups.filter(g => g.dropdown && g.items.length > 0);

  const [openId, setOpenId] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close when navigating
  useEffect(() => {
    setOpenId(null);
  }, [location.pathname]);

  return (
    <nav className="hidden md:flex items-center space-x-1 lg:space-x-2" ref={navRef}>
      {flatItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          icon={item.icon}
          label={item.label}
          showIcon={false}
          className="text-[clamp(0.7rem,1.5vw,0.875rem)] px-2 py-1"
        />
      ))}

      {dropdownGroups.map((group) => {
        const isActive = group.items.some(item => item.path === location.pathname);
        const open = openId === group.id;
        return (
          <div className="relative" key={group.id}>
            <button
              onClick={() => setOpenId(open ? null : group.id)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[clamp(0.7rem,1.5vw,0.875rem)] transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
            >
              {group.label}
              <FiChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
              <div className="absolute right-0 z-50 mt-2 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    icon={item.icon}
                    label={item.label}
                    showIcon={true}
                    onClick={() => setOpenId(null)}
                    className="text-sm mx-1"
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {isAuthenticated && customerName && (
        <span className="text-gray-400 text-[clamp(0.7rem,1.5vw,0.875rem)] px-2 truncate max-w-[140px]">
          👤 {customerName}
        </span>
      )}
      {isAuthenticated && (
        <button
          onClick={onLogout}
          className="text-gray-300 hover:text-white hover:bg-gray-700 px-2 py-1 rounded-md text-[clamp(0.7rem,1.5vw,0.875rem)] transition-colors"
        >
          Logout
        </button>
      )}
    </nav>
  );
};

export default DesktopNav;
